/**
 * Machine-readable error codes. Every API error returns one of these.
 * Frontend can switch on the code to show appropriate UI.
 */
export const ERROR_CODES = {
    // Auth & Access
    UNAUTHORIZED: "UNAUTHORIZED",
    FORBIDDEN: "FORBIDDEN",
    INVALID_TOKEN: "INVALID_TOKEN",
    TENANT_NOT_FOUND: "TENANT_NOT_FOUND",
    TENANT_SCHEMA_MISSING: "TENANT_SCHEMA_MISSING",
    // Personal Access Tokens (CLI auth)
    TOKEN_INVALID: "TOKEN_INVALID",
    SCOPE_INSUFFICIENT: "SCOPE_INSUFFICIENT",
    TOKEN_NOT_FOUND: "TOKEN_NOT_FOUND",
    // Validation
    VALIDATION_ERROR: "VALIDATION_ERROR",
    INVALID_INPUT: "INVALID_INPUT",
    // Contacts
    CONTACT_NOT_FOUND: "CONTACT_NOT_FOUND",
    CONTACT_DUPLICATE_EMAIL: "CONTACT_DUPLICATE_EMAIL",
    CONTACT_MERGE_FAILED: "CONTACT_MERGE_FAILED",
    // Contact Groups
    CONTACT_GROUP_NOT_FOUND: "CONTACT_GROUP_NOT_FOUND",
    CONTACT_GROUP_DUPLICATE_NAME: "CONTACT_GROUP_DUPLICATE_NAME",
    // Companies
    COMPANY_NOT_FOUND: "COMPANY_NOT_FOUND",
    COMPANY_MERGE_FAILED: "COMPANY_MERGE_FAILED",
    // Board Meetings
    BOARD_MEETING_NOT_FOUND: "BOARD_MEETING_NOT_FOUND",
    BOARD_MEETING_INVALID_TRANSITION: "BOARD_MEETING_INVALID_TRANSITION",
    BOARD_MEETING_NOT_DRAFT: "BOARD_MEETING_NOT_DRAFT",
    BOARD_SNAPSHOT_FAILED: "BOARD_SNAPSHOT_FAILED",
    BOARD_PACK_GENERATION_FAILED: "BOARD_PACK_GENERATION_FAILED",
    // Documents & Vault
    DOCUMENT_NOT_FOUND: "DOCUMENT_NOT_FOUND",
    FOLDER_NOT_FOUND: "FOLDER_NOT_FOUND",
    DOCUMENT_ACCESS_DENIED: "DOCUMENT_ACCESS_DENIED",
    DOCUMENT_LOCKED: "DOCUMENT_LOCKED",
    UPLOAD_FAILED: "UPLOAD_FAILED",
    OCR_FAILED: "OCR_FAILED",
    // Signatures
    SIGNATURE_REQUEST_NOT_FOUND: "SIGNATURE_REQUEST_NOT_FOUND",
    SIGNATURE_ALREADY_SIGNED: "SIGNATURE_ALREADY_SIGNED",
    SIGNATURE_EXPIRED: "SIGNATURE_EXPIRED",
    SIGNATURE_DOCUMENT_TAMPERED: "SIGNATURE_DOCUMENT_TAMPERED",
    SIGNATURE_INVALID_TOKEN: "SIGNATURE_INVALID_TOKEN",
    SIGNATURE_NOT_ACTIVE: "SIGNATURE_NOT_ACTIVE",
    // Agreement dossiers (lifecycle orchestration spine)
    AGREEMENT_NOT_FOUND: "AGREEMENT_NOT_FOUND",
    AGREEMENT_INVALID_TRANSITION: "AGREEMENT_INVALID_TRANSITION",
    // Messaging
    CHANNEL_NOT_FOUND: "CHANNEL_NOT_FOUND",
    MESSAGE_NOT_FOUND: "MESSAGE_NOT_FOUND",
    MEETING_RECORDING_NOT_FOUND: "MEETING_RECORDING_NOT_FOUND",
    NOT_CHANNEL_MEMBER: "NOT_CHANNEL_MEMBER",
    WEB_PUSH_NOT_CONFIGURED: "WEB_PUSH_NOT_CONFIGURED",
    WEB_PUSH_SUBSCRIPTION_NOT_FOUND: "WEB_PUSH_SUBSCRIPTION_NOT_FOUND",
    // Email
    EMAIL_SEND_FAILED: "EMAIL_SEND_FAILED",
    EMAIL_TRACKING_INVALID: "EMAIL_TRACKING_INVALID",
    // Authenticator
    AUTHENTICATOR_ENTRY_NOT_FOUND: "AUTHENTICATOR_ENTRY_NOT_FOUND",
    // Cashflow & QBO
    QBO_NOT_CONNECTED: "QBO_NOT_CONNECTED",
    QBO_TOKEN_EXPIRED: "QBO_TOKEN_EXPIRED",
    QBO_API_ERROR: "QBO_API_ERROR",
    CASHFLOW_ITEM_NOT_FOUND: "CASHFLOW_ITEM_NOT_FOUND",
    RECEIPT_NOT_FOUND: "RECEIPT_NOT_FOUND",
    // Portal admin (staff managing portal users)
    PORTAL_USER_NOT_FOUND: "PORTAL_USER_NOT_FOUND",
    PORTAL_USER_DUPLICATE_EMAIL: "PORTAL_USER_DUPLICATE_EMAIL",
    // Notes (feature #1)
    NOTE_NOT_FOUND: "NOTE_NOT_FOUND",
    // Subject associations (Phase 3)
    SUBJECT_ASSOCIATION_NOT_FOUND: "SUBJECT_ASSOCIATION_NOT_FOUND",
    SUBJECT_ASSOCIATION_EXISTS: "SUBJECT_ASSOCIATION_EXISTS",
    SUBJECT_KNOWLEDGE_SCOPE_NOT_FOUND: "SUBJECT_KNOWLEDGE_SCOPE_NOT_FOUND",
    SUBJECT_KNOWLEDGE_SCOPE_EXISTS: "SUBJECT_KNOWLEDGE_SCOPE_EXISTS",
    SUBJECT_KNOWLEDGE_SCOPE_SELF: "SUBJECT_KNOWLEDGE_SCOPE_SELF",
    SUBJECT_KNOWLEDGE_CANDIDATE_NOT_FOUND: "SUBJECT_KNOWLEDGE_CANDIDATE_NOT_FOUND",
    SUBJECT_KNOWLEDGE_CANDIDATE_INVALID: "SUBJECT_KNOWLEDGE_CANDIDATE_INVALID",
    SUBJECT_KNOWLEDGE_CANDIDATE_COMPETING: "SUBJECT_KNOWLEDGE_CANDIDATE_COMPETING",
    SUBJECT_KNOWLEDGE_CANDIDATE_HISTORICAL_UNCONFIRMED: "SUBJECT_KNOWLEDGE_CANDIDATE_HISTORICAL_UNCONFIRMED",
    SUBJECT_KNOWLEDGE_CANDIDATE_TERMINAL: "SUBJECT_KNOWLEDGE_CANDIDATE_TERMINAL",
    SUBJECT_KNOWLEDGE_CANDIDATE_FACTS_CAP: "SUBJECT_KNOWLEDGE_CANDIDATE_FACTS_CAP",
    SUBJECT_KNOWLEDGE_CANDIDATE_EVIDENCE_RESTRICTED: "SUBJECT_KNOWLEDGE_CANDIDATE_EVIDENCE_RESTRICTED",
    // Premium Ideation
    IDEATION_BOARD_NOT_FOUND: "IDEATION_BOARD_NOT_FOUND",
    IDEATION_CLUSTER_NOT_FOUND: "IDEATION_CLUSTER_NOT_FOUND",
    // Engagement plan (Sprint B 7b')
    ENGAGEMENT_NOT_FOUND: "ENGAGEMENT_NOT_FOUND",
    STAKEHOLDER_NOT_FOUND: "STAKEHOLDER_NOT_FOUND",
    STAKEHOLDER_DUPLICATE_EMAIL: "STAKEHOLDER_DUPLICATE_EMAIL",
    MILESTONE_NOT_FOUND: "MILESTONE_NOT_FOUND",
    // Programme Collaboration Plane (GoP-style bodies/members)
    PROGRAMME_BODY_NOT_FOUND: "PROGRAMME_BODY_NOT_FOUND",
    PROGRAMME_BODY_MEMBER_NOT_FOUND: "PROGRAMME_BODY_MEMBER_NOT_FOUND",
    // Programme Collaboration Plane — meeting ceremony (Task 8)
    PROGRAMME_MEETING_NOT_FOUND: "PROGRAMME_MEETING_NOT_FOUND",
    PROGRAMME_MEETING_INVALID_TRANSITION: "PROGRAMME_MEETING_INVALID_TRANSITION",
    PROGRAMME_MEETING_AGENDA_ITEM_NOT_FOUND: "PROGRAMME_MEETING_AGENDA_ITEM_NOT_FOUND",
    PROGRAMME_MEETING_ACTION_NOT_FOUND: "PROGRAMME_MEETING_ACTION_NOT_FOUND",
    // Premium portal / Client Hub
    PORTAL_WORKSPACE_NOT_FOUND: "PORTAL_WORKSPACE_NOT_FOUND",
    WORKSPACE_LINK_CONFLICT: "WORKSPACE_LINK_CONFLICT",
    PORTAL_BRIEF_NOT_FOUND: "PORTAL_BRIEF_NOT_FOUND",
    PORTAL_THREAD_NOT_FOUND: "PORTAL_THREAD_NOT_FOUND",
    PORTAL_INBOX_ITEM_NOT_FOUND: "PORTAL_INBOX_ITEM_NOT_FOUND",
    PORTAL_OBJECT_ACCESS_DENIED: "PORTAL_OBJECT_ACCESS_DENIED",
    PORTAL_PUBLICATION_NOT_FOUND: "PORTAL_PUBLICATION_NOT_FOUND",
    // Shared-document review (portal external document review feature)
    SHARED_DOC_INVARIANT: "SHARED_DOC_INVARIANT",
    SHARED_SOURCE_UNAVAILABLE: "SHARED_SOURCE_UNAVAILABLE",
    // "New partner portal" onboarding wizard
    PORTAL_ONBOARDING_RUN_NOT_FOUND: "PORTAL_ONBOARDING_RUN_NOT_FOUND",
    PORTAL_ONBOARDING_CONFIRMATION_MISMATCH: "PORTAL_ONBOARDING_CONFIRMATION_MISMATCH",
    PORTAL_ONBOARDING_PREVIEW_STALE: "PORTAL_ONBOARDING_PREVIEW_STALE",
    PORTAL_ONBOARDING_RUN_IN_PROGRESS: "PORTAL_ONBOARDING_RUN_IN_PROGRESS",
    PORTAL_ONBOARDING_STEP_FAILED: "PORTAL_ONBOARDING_STEP_FAILED",
    // Sharing
    SHARE_NOT_FOUND: "SHARE_NOT_FOUND",
    SHARE_EXPIRED: "SHARE_EXPIRED",
    SHARE_ACCESS_DENIED: "SHARE_ACCESS_DENIED",
    // Scheduling (feature #3 Calendly)
    EVENT_TYPE_NOT_FOUND: "EVENT_TYPE_NOT_FOUND",
    EVENT_TYPE_DUPLICATE_SLUG: "EVENT_TYPE_DUPLICATE_SLUG",
    AVAILABILITY_RULE_NOT_FOUND: "AVAILABILITY_RULE_NOT_FOUND",
    AVAILABILITY_OVERRIDE_NOT_FOUND: "AVAILABILITY_OVERRIDE_NOT_FOUND",
    BOOKING_NOT_FOUND: "BOOKING_NOT_FOUND",
    BOOKING_SLOT_UNAVAILABLE: "BOOKING_SLOT_UNAVAILABLE",
    BOOKING_TURNSTILE_FAILED: "BOOKING_TURNSTILE_FAILED",
    ENQUIRY_TURNSTILE_FAILED: "ENQUIRY_TURNSTILE_FAILED",
    MAGIC_LINK_TURNSTILE_FAILED: "MAGIC_LINK_TURNSTILE_FAILED",
    GRAPH_CALENDAR_ERROR: "GRAPH_CALENDAR_ERROR",
    // Time tracking (feature #5)
    TIME_ENTRY_NOT_FOUND: "TIME_ENTRY_NOT_FOUND",
    TIME_TRACKING_ALREADY_RUNNING: "TIME_TRACKING_ALREADY_RUNNING",
    TIME_TRACKING_NOT_RUNNING: "TIME_TRACKING_NOT_RUNNING",
    TIME_TRACKING_INVALID_RANGE: "TIME_TRACKING_INVALID_RANGE",
    // AI
    AI_PROVIDER_ERROR: "AI_PROVIDER_ERROR",
    AI_RATE_LIMITED: "AI_RATE_LIMITED",
    AI_BUDGET_EXCEEDED: "AI_BUDGET_EXCEEDED",
    // Generic
    NOT_FOUND: "NOT_FOUND",
    RATE_LIMITED: "RATE_LIMITED",
    INTERNAL_ERROR: "INTERNAL_ERROR",
    SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
};
/** HTTP status code mapping for each error code */
export const ERROR_STATUS_MAP = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    INVALID_TOKEN: 401,
    TENANT_NOT_FOUND: 404,
    TENANT_SCHEMA_MISSING: 500,
    TOKEN_INVALID: 401,
    SCOPE_INSUFFICIENT: 403,
    TOKEN_NOT_FOUND: 404,
    VALIDATION_ERROR: 400,
    INVALID_INPUT: 400,
    CONTACT_NOT_FOUND: 404,
    CONTACT_DUPLICATE_EMAIL: 409,
    CONTACT_MERGE_FAILED: 422,
    CONTACT_GROUP_NOT_FOUND: 404,
    CONTACT_GROUP_DUPLICATE_NAME: 409,
    COMPANY_NOT_FOUND: 404,
    COMPANY_MERGE_FAILED: 422,
    BOARD_MEETING_NOT_FOUND: 404,
    BOARD_MEETING_INVALID_TRANSITION: 422,
    BOARD_MEETING_NOT_DRAFT: 422,
    BOARD_SNAPSHOT_FAILED: 500,
    BOARD_PACK_GENERATION_FAILED: 500,
    DOCUMENT_NOT_FOUND: 404,
    FOLDER_NOT_FOUND: 404,
    DOCUMENT_ACCESS_DENIED: 403,
    DOCUMENT_LOCKED: 423,
    UPLOAD_FAILED: 500,
    OCR_FAILED: 500,
    SIGNATURE_REQUEST_NOT_FOUND: 404,
    SIGNATURE_ALREADY_SIGNED: 409,
    SIGNATURE_EXPIRED: 410,
    SIGNATURE_DOCUMENT_TAMPERED: 422,
    SIGNATURE_INVALID_TOKEN: 401,
    SIGNATURE_NOT_ACTIVE: 409,
    AGREEMENT_NOT_FOUND: 404,
    AGREEMENT_INVALID_TRANSITION: 422,
    CHANNEL_NOT_FOUND: 404,
    MESSAGE_NOT_FOUND: 404,
    MEETING_RECORDING_NOT_FOUND: 404,
    NOT_CHANNEL_MEMBER: 403,
    WEB_PUSH_NOT_CONFIGURED: 503,
    WEB_PUSH_SUBSCRIPTION_NOT_FOUND: 404,
    EMAIL_SEND_FAILED: 500,
    EMAIL_TRACKING_INVALID: 400,
    AUTHENTICATOR_ENTRY_NOT_FOUND: 404,
    QBO_NOT_CONNECTED: 422,
    QBO_TOKEN_EXPIRED: 401,
    QBO_API_ERROR: 502,
    CASHFLOW_ITEM_NOT_FOUND: 404,
    RECEIPT_NOT_FOUND: 404,
    PORTAL_USER_NOT_FOUND: 404,
    PORTAL_USER_DUPLICATE_EMAIL: 409,
    NOTE_NOT_FOUND: 404,
    SUBJECT_ASSOCIATION_NOT_FOUND: 404,
    SUBJECT_ASSOCIATION_EXISTS: 409,
    SUBJECT_KNOWLEDGE_SCOPE_NOT_FOUND: 404,
    SUBJECT_KNOWLEDGE_SCOPE_EXISTS: 409,
    SUBJECT_KNOWLEDGE_SCOPE_SELF: 400,
    SUBJECT_KNOWLEDGE_CANDIDATE_NOT_FOUND: 404,
    SUBJECT_KNOWLEDGE_CANDIDATE_INVALID: 400,
    SUBJECT_KNOWLEDGE_CANDIDATE_COMPETING: 409,
    SUBJECT_KNOWLEDGE_CANDIDATE_HISTORICAL_UNCONFIRMED: 409,
    SUBJECT_KNOWLEDGE_CANDIDATE_TERMINAL: 409,
    SUBJECT_KNOWLEDGE_CANDIDATE_FACTS_CAP: 422,
    SUBJECT_KNOWLEDGE_CANDIDATE_EVIDENCE_RESTRICTED: 409,
    IDEATION_BOARD_NOT_FOUND: 404,
    IDEATION_CLUSTER_NOT_FOUND: 404,
    ENGAGEMENT_NOT_FOUND: 404,
    STAKEHOLDER_NOT_FOUND: 404,
    STAKEHOLDER_DUPLICATE_EMAIL: 409,
    MILESTONE_NOT_FOUND: 404,
    PROGRAMME_BODY_NOT_FOUND: 404,
    PROGRAMME_BODY_MEMBER_NOT_FOUND: 404,
    PROGRAMME_MEETING_NOT_FOUND: 404,
    PROGRAMME_MEETING_INVALID_TRANSITION: 422,
    PROGRAMME_MEETING_AGENDA_ITEM_NOT_FOUND: 404,
    PROGRAMME_MEETING_ACTION_NOT_FOUND: 404,
    PORTAL_WORKSPACE_NOT_FOUND: 404,
    WORKSPACE_LINK_CONFLICT: 409,
    PORTAL_BRIEF_NOT_FOUND: 404,
    PORTAL_THREAD_NOT_FOUND: 404,
    PORTAL_INBOX_ITEM_NOT_FOUND: 404,
    PORTAL_OBJECT_ACCESS_DENIED: 403,
    PORTAL_PUBLICATION_NOT_FOUND: 404,
    SHARED_DOC_INVARIANT: 400,
    SHARED_SOURCE_UNAVAILABLE: 404,
    PORTAL_ONBOARDING_RUN_NOT_FOUND: 404,
    PORTAL_ONBOARDING_CONFIRMATION_MISMATCH: 400,
    PORTAL_ONBOARDING_PREVIEW_STALE: 409,
    PORTAL_ONBOARDING_RUN_IN_PROGRESS: 409,
    PORTAL_ONBOARDING_STEP_FAILED: 500,
    SHARE_NOT_FOUND: 404,
    SHARE_EXPIRED: 410,
    SHARE_ACCESS_DENIED: 403,
    EVENT_TYPE_NOT_FOUND: 404,
    EVENT_TYPE_DUPLICATE_SLUG: 409,
    AVAILABILITY_RULE_NOT_FOUND: 404,
    AVAILABILITY_OVERRIDE_NOT_FOUND: 404,
    BOOKING_NOT_FOUND: 404,
    BOOKING_SLOT_UNAVAILABLE: 409,
    BOOKING_TURNSTILE_FAILED: 403,
    ENQUIRY_TURNSTILE_FAILED: 403,
    MAGIC_LINK_TURNSTILE_FAILED: 403,
    GRAPH_CALENDAR_ERROR: 502,
    TIME_ENTRY_NOT_FOUND: 404,
    TIME_TRACKING_ALREADY_RUNNING: 409,
    TIME_TRACKING_NOT_RUNNING: 409,
    TIME_TRACKING_INVALID_RANGE: 400,
    AI_PROVIDER_ERROR: 502,
    AI_RATE_LIMITED: 429,
    AI_BUDGET_EXCEEDED: 429,
    NOT_FOUND: 404,
    RATE_LIMITED: 429,
    INTERNAL_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
};
/**
 * User-facing message for each error code.
 *
 * Canon: docs/design-canon/01-ui/feedback-states.md R5 +
 *        docs/design-canon/04-shared/error-codes.md.
 *
 * Per canon R6 of error-codes.md, frontend switches on `code`, not
 * `message`. Server-side message text is incidental — this map is the
 * authoritative source for what a user sees when only the code is known.
 *
 * Style:
 *  - Action-oriented when the user can do something ("Sign in again").
 *  - Honest-and-brief when the cause is upstream/internal.
 *  - Never leak schema names, stack traces, or implementation details.
 */
export const ERROR_MESSAGES = {
    UNAUTHORIZED: "You need to sign in to continue.",
    FORBIDDEN: "You don't have permission to do that.",
    INVALID_TOKEN: "Your session has expired. Sign in again.",
    TENANT_NOT_FOUND: "We couldn't find your workspace. Contact support.",
    TENANT_SCHEMA_MISSING: "Your workspace isn't fully provisioned. Contact support.",
    TOKEN_INVALID: "That access token isn't valid. Re-issue one from Settings → Tokens.",
    SCOPE_INSUFFICIENT: "This token doesn't have permission to do that. Re-issue it with the right scope.",
    TOKEN_NOT_FOUND: "That access token no longer exists.",
    VALIDATION_ERROR: "Some fields aren't filled in correctly.",
    INVALID_INPUT: "That input isn't valid.",
    CONTACT_NOT_FOUND: "That contact no longer exists.",
    CONTACT_DUPLICATE_EMAIL: "A contact with that email already exists.",
    CONTACT_MERGE_FAILED: "Couldn't merge those contacts. Try again.",
    CONTACT_GROUP_NOT_FOUND: "That contact group no longer exists.",
    CONTACT_GROUP_DUPLICATE_NAME: "A contact group with that name already exists.",
    COMPANY_NOT_FOUND: "That company no longer exists.",
    COMPANY_MERGE_FAILED: "Couldn't merge those companies. Try again.",
    BOARD_MEETING_NOT_FOUND: "That board meeting no longer exists.",
    BOARD_MEETING_INVALID_TRANSITION: "That status change isn't allowed from the meeting's current state.",
    BOARD_MEETING_NOT_DRAFT: "Only draft board meetings can be edited.",
    BOARD_SNAPSHOT_FAILED: "Couldn't snapshot the board pack. Try again or contact support.",
    BOARD_PACK_GENERATION_FAILED: "Couldn't generate the board pack PDF. Try again.",
    DOCUMENT_NOT_FOUND: "That document no longer exists.",
    FOLDER_NOT_FOUND: "That folder no longer exists.",
    DOCUMENT_ACCESS_DENIED: "You don't have access to that document.",
    DOCUMENT_LOCKED: "That document is open in Office or was just changed by someone else. Try again once they're done.",
    UPLOAD_FAILED: "Upload failed. Check your connection and try again.",
    OCR_FAILED: "Couldn't read text from that document.",
    SIGNATURE_REQUEST_NOT_FOUND: "That signature request no longer exists.",
    SIGNATURE_ALREADY_SIGNED: "This document has already been signed.",
    SIGNATURE_EXPIRED: "This signature link has expired.",
    SIGNATURE_DOCUMENT_TAMPERED: "This document doesn't match what was sent for signing.",
    SIGNATURE_INVALID_TOKEN: "This signature link isn't valid.",
    SIGNATURE_NOT_ACTIVE: "This signature request is no longer active.",
    AGREEMENT_NOT_FOUND: "That agreement dossier no longer exists.",
    AGREEMENT_INVALID_TRANSITION: "That status change isn't allowed from the agreement's current state.",
    CHANNEL_NOT_FOUND: "That channel no longer exists.",
    MESSAGE_NOT_FOUND: "That message no longer exists.",
    MEETING_RECORDING_NOT_FOUND: "That meeting recording no longer exists.",
    NOT_CHANNEL_MEMBER: "You're not a member of that channel.",
    WEB_PUSH_NOT_CONFIGURED: "Push notifications aren't configured on this server. Contact your admin.",
    WEB_PUSH_SUBSCRIPTION_NOT_FOUND: "That push subscription no longer exists.",
    EMAIL_SEND_FAILED: "Couldn't send the email. Try again.",
    EMAIL_TRACKING_INVALID: "That tracking link isn't valid.",
    AUTHENTICATOR_ENTRY_NOT_FOUND: "That authenticator entry no longer exists.",
    QBO_NOT_CONNECTED: "QuickBooks isn't connected. Connect it in Settings.",
    QBO_TOKEN_EXPIRED: "QuickBooks needs to be reconnected. Visit Settings.",
    QBO_API_ERROR: "QuickBooks returned an error. Try again in a moment.",
    CASHFLOW_ITEM_NOT_FOUND: "That cashflow item no longer exists.",
    RECEIPT_NOT_FOUND: "That receipt no longer exists.",
    PORTAL_USER_NOT_FOUND: "That portal user no longer exists.",
    PORTAL_USER_DUPLICATE_EMAIL: "A portal user with that email already exists in this tenant.",
    NOTE_NOT_FOUND: "That note no longer exists.",
    SUBJECT_ASSOCIATION_NOT_FOUND: "That subject association no longer exists.",
    SUBJECT_ASSOCIATION_EXISTS: "That object is already associated with this subject.",
    SUBJECT_KNOWLEDGE_SCOPE_NOT_FOUND: "That knowledge-scope inclusion no longer exists.",
    SUBJECT_KNOWLEDGE_SCOPE_EXISTS: "That origin is already included for this subject.",
    SUBJECT_KNOWLEDGE_SCOPE_SELF: "A subject cannot include knowledge from itself.",
    SUBJECT_KNOWLEDGE_CANDIDATE_NOT_FOUND: "That pending knowledge item no longer exists.",
    SUBJECT_KNOWLEDGE_CANDIDATE_INVALID: "That pending knowledge item isn't valid.",
    SUBJECT_KNOWLEDGE_CANDIDATE_COMPETING: "This proposal disagrees with what is already recorded for the same day. Confirm if you mean to keep both.",
    SUBJECT_KNOWLEDGE_CANDIDATE_HISTORICAL_UNCONFIRMED: "This proposal is older than what is currently recorded. Confirm if you mean to keep it as history.",
    SUBJECT_KNOWLEDGE_CANDIDATE_TERMINAL: "That pending knowledge item has already been reviewed.",
    SUBJECT_KNOWLEDGE_CANDIDATE_FACTS_CAP: "This subject already has the maximum number of recorded facts.",
    SUBJECT_KNOWLEDGE_CANDIDATE_EVIDENCE_RESTRICTED: "The source for that pending item can't be verified.",
    IDEATION_BOARD_NOT_FOUND: "That ideation board no longer exists.",
    IDEATION_CLUSTER_NOT_FOUND: "That ideation cluster no longer exists.",
    ENGAGEMENT_NOT_FOUND: "That engagement no longer exists.",
    STAKEHOLDER_NOT_FOUND: "That stakeholder no longer exists.",
    STAKEHOLDER_DUPLICATE_EMAIL: "A stakeholder with that email is already on this engagement.",
    MILESTONE_NOT_FOUND: "That milestone no longer exists.",
    PROGRAMME_BODY_NOT_FOUND: "That programme body no longer exists.",
    PROGRAMME_BODY_MEMBER_NOT_FOUND: "That programme body member no longer exists.",
    PROGRAMME_MEETING_NOT_FOUND: "That meeting no longer exists.",
    PROGRAMME_MEETING_INVALID_TRANSITION: "That status change isn't allowed from the meeting's current state.",
    PROGRAMME_MEETING_AGENDA_ITEM_NOT_FOUND: "That agenda item no longer exists.",
    PROGRAMME_MEETING_ACTION_NOT_FOUND: "That action item no longer exists.",
    PORTAL_WORKSPACE_NOT_FOUND: "That portal workspace no longer exists.",
    WORKSPACE_LINK_CONFLICT: "Another active workspace is already linked to that board meeting or project.",
    PORTAL_BRIEF_NOT_FOUND: "That portal brief no longer exists.",
    PORTAL_THREAD_NOT_FOUND: "That portal thread no longer exists.",
    PORTAL_INBOX_ITEM_NOT_FOUND: "That portal inbox item no longer exists.",
    PORTAL_OBJECT_ACCESS_DENIED: "You don't have access to that portal item.",
    PORTAL_PUBLICATION_NOT_FOUND: "That programme is not available.",
    SHARED_DOC_INVARIANT: "A working-document share must reference a version.",
    SHARED_SOURCE_UNAVAILABLE: "This document is no longer available.",
    PORTAL_ONBOARDING_RUN_NOT_FOUND: "That partner-portal provisioning run no longer exists.",
    PORTAL_ONBOARDING_CONFIRMATION_MISMATCH: "Type the tenant slug exactly to confirm provisioning.",
    PORTAL_ONBOARDING_PREVIEW_STALE: "The wizard changed since the preview was generated. Review the preview again.",
    PORTAL_ONBOARDING_RUN_IN_PROGRESS: "That provisioning run is still in progress.",
    PORTAL_ONBOARDING_STEP_FAILED: "A provisioning step failed. Review the run and retry.",
    SHARE_NOT_FOUND: "That share link no longer exists.",
    SHARE_EXPIRED: "This share link has expired.",
    SHARE_ACCESS_DENIED: "You don't have access via this share link.",
    EVENT_TYPE_NOT_FOUND: "That event type no longer exists.",
    EVENT_TYPE_DUPLICATE_SLUG: "You already have an event type with that slug.",
    AVAILABILITY_RULE_NOT_FOUND: "That availability rule no longer exists.",
    AVAILABILITY_OVERRIDE_NOT_FOUND: "That availability override no longer exists.",
    BOOKING_NOT_FOUND: "That booking no longer exists.",
    BOOKING_SLOT_UNAVAILABLE: "That slot is no longer available. Pick another time.",
    BOOKING_TURNSTILE_FAILED: "We couldn't verify your booking attempt. Try again.",
    ENQUIRY_TURNSTILE_FAILED: "We couldn't verify your enquiry. Please retry.",
    MAGIC_LINK_TURNSTILE_FAILED: "We couldn't verify your sign-in attempt. Please retry.",
    GRAPH_CALENDAR_ERROR: "Calendar sync failed. The booking was saved; we'll retry shortly.",
    TIME_ENTRY_NOT_FOUND: "That time entry no longer exists.",
    TIME_TRACKING_ALREADY_RUNNING: "You already have a timer running. Stop it before starting a new one.",
    TIME_TRACKING_NOT_RUNNING: "No timer is running.",
    TIME_TRACKING_INVALID_RANGE: "End time must be after start time.",
    AI_PROVIDER_ERROR: "The AI service returned an error. Try again in a moment.",
    AI_RATE_LIMITED: "Too many AI requests right now. Try again in a moment.",
    AI_BUDGET_EXCEEDED: "Insights AI monthly token budget has been reached. Ask an admin to raise the cap or try again next month.",
    NOT_FOUND: "That couldn't be found.",
    RATE_LIMITED: "Too many requests. Try again in a moment.",
    INTERNAL_ERROR: "Something went wrong on our side. Try again or contact support.",
    SERVICE_UNAVAILABLE: "The service is temporarily unavailable. Try again in a moment.",
};
/**
 * Resolve an error code to a user-facing message.
 *
 * Accepts unknown codes (e.g. an external service returning a string the
 * frontend doesn't recognise) and falls back to the generic INTERNAL_ERROR
 * message — the UI surface still shows the raw `code` next to it via
 * `<ErrorState code={code} />`, so support can still grep logs.
 */
export function errorMessageFor(code) {
    if (code && code in ERROR_MESSAGES) {
        return ERROR_MESSAGES[code];
    }
    return ERROR_MESSAGES.INTERNAL_ERROR;
}
//# sourceMappingURL=error-codes.js.map